import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const ManageBannersInner = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ image_url: "", link_url: "", display_order: 0 });
    const [editingId, setEditingId] = useState(null);
    const token = localStorage.getItem("adminToken");
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        if (!token) {
            navigate("/winrox11-super-admin");
        } else {
            fetchBanners();
        }
    }, [token, navigate]);

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/banners/admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBanners(res.data);
        } catch (err) {
            toast.error("Failed to fetch banners");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.image_url) return toast.warn("Image URL is required");

        try {
            if (editingId) {
                await axios.put(`${API_URL}/banners/${editingId}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Banner updated successfully");
            } else {
                await axios.post(`${API_URL}/banners`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Banner added successfully");
            }
            setFormData({ image_url: "", link_url: "", display_order: 0 });
            setEditingId(null);
            fetchBanners();
        } catch (err) {
            toast.error("Failed to save banner");
        }
    };

    const handleEdit = (banner) => {
        setFormData({
            image_url: banner.image_url,
            link_url: banner.link_url || "",
            display_order: banner.display_order || 0
        });
        setEditingId(banner.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this banner?")) return;
        try {
            await axios.delete(`${API_URL}/banners/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Banner deleted");
            fetchBanners();
        } catch (err) {
            toast.error("Failed to delete banner");
        }
    };

    const toggleStatus = async (banner) => {
        try {
            await axios.put(`${API_URL}/banners/${banner.id}`, {
                ...banner,
                is_active: !banner.is_active
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchBanners();
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    return (
        <div style={S.page}>
            <style>{GLOBAL_ADMIN_CSS}</style>
            
            <div style={S.topBar}>
                <div>
                    <div style={S.pageTitle}>Manage Ad Banners</div>
                    <div style={S.pageSubtitle}>Add or update promotional banners for the home page</div>
                </div>
                <button onClick={() => navigate("/winrox11-super-admin")} style={S.btnCancel}>Back to Dashboard</button>
            </div>

            <div style={S.card}>
                <div style={S.cardTitle}>{editingId ? "Edit Banner" : "Add New Banner"}</div>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: "16px" }}>
                        <label style={S.label}>Image URL *</label>
                        <input 
                            style={S.input} 
                            placeholder="https://example.com/banner.jpg"
                            value={formData.image_url}
                            onChange={e => setFormData({...formData, image_url: e.target.value})}
                            required
                        />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                        <div>
                            <label style={S.label}>Redirect Link (optional)</label>
                            <input 
                                style={S.input} 
                                placeholder="https://winrox11.com/offer"
                                value={formData.link_url}
                                onChange={e => setFormData({...formData, link_url: e.target.value})}
                            />
                        </div>
                        <div>
                            <label style={S.label}>Display Order</label>
                            <input 
                                type="number"
                                style={S.input} 
                                value={formData.display_order}
                                onChange={e => setFormData({...formData, display_order: parseInt(e.target.value)})}
                            />
                        </div>
                    </div>
                    <div style={S.btnRow}>
                        {editingId && (
                            <button 
                                type="button" 
                                style={S.btnCancel} 
                                onClick={() => {
                                    setEditingId(null);
                                    setFormData({ image_url: "", link_url: "", display_order: 0 });
                                }}
                            >
                                Cancel Edit
                            </button>
                        )}
                        <button type="submit" style={S.btnPrimary}>
                            {editingId ? "Update Banner" : "Add Banner"}
                        </button>
                    </div>
                </form>
            </div>

            <div style={S.sectionTitle}>Existing Banners ({banners.length})</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
                {banners.map(banner => (
                    <div key={banner.id} style={{ 
                        background: COLORS.card, 
                        borderRadius: "16px", 
                        overflow: "hidden", 
                        border: `1px solid ${COLORS.cardBorder}`,
                        display: "flex",
                        flexDirection: "column"
                    }}>
                        <div style={{ height: "150px", overflow: "hidden", background: "#000" }}>
                            <img src={banner.image_url} alt="Banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <div style={{ padding: "16px" }}>
                            <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.text, marginBottom: "4px", wordBreak: "break-all" }}>
                                {banner.link_url || "No link"}
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <button 
                                        onClick={() => toggleStatus(banner)}
                                        style={{ 
                                            ...S.badge(banner.is_active ? COLORS.green : COLORS.red),
                                            border: "none",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {banner.is_active ? "Active" : "Inactive"}
                                    </button>
                                    <span style={S.badge(COLORS.indigo)}>Order: {banner.display_order}</span>
                                </div>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button onClick={() => handleEdit(banner)} style={{ ...S.miniBtn, color: COLORS.indigo }}>Edit</button>
                                    <button onClick={() => handleDelete(banner.id)} style={{ ...S.miniBtn, color: COLORS.red }}>Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {banners.length === 0 && !loading && (
                    <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px", color: COLORS.textMuted }}>
                        No banners found. Add your first banner above!
                    </div>
                )}
            </div>
        </div>
    );
};

const ManageBanners = () => (
    <ToastProvider>
        <ManageBannersInner />
    </ToastProvider>
);

export default ManageBanners;
